/**
 * The Congress tab's pure half: amounts, dates and the window choice
 * (`docs/plans/congress-trades.md`). No React here but the one load hook.
 */
import { useEffect, useState } from "react";

import { loadCongress, type CongressSymbol, type CongressTrade } from "../api/congress";

/** 90 days by default, a 1 year switch (owner, 2026-10-14). */
export const WINDOWS = [
  { days: 90, label: "90 days" },
  { days: 366, label: "1 year" },
] as const;

/** `15001` -> `$15K`, `1000001` -> `$1M`, `50000` -> `$50K`. Filed ranges start at x,001. */
export function shortUsd(v: number): string {
  const r = v % 1000 === 1 ? v - 1 : v;
  if (r >= 1_000_000) return `$${trim(r / 1_000_000)}M`;
  if (r >= 1_000) return `$${trim(r / 1_000)}K`;
  return `$${r}`;
}

function trim(x: number): string {
  return x >= 10 || Number.isInteger(x) ? String(Math.round(x)) : x.toFixed(1);
}

/** A trade's filed range: `$15K–50K`, `Over $50M`; the filed text when it cannot be read. */
export function rangeLabel(low: number | null, high: number | null, filed: string | null = null): string {
  if (low == null) return filed ?? "—";
  if (high == null) return `Over ${shortUsd(low)}`;
  return `${shortUsd(low)}–${shortUsd(high).slice(1)}`;
}

/** A tile's total: the sum of the ranges. `+` when an open-ended range is in it. */
export function totalLabel(low: number | null, high: number | null, count: number): string {
  if (!count || low == null) return "—";
  return high == null ? `${shortUsd(low)}+` : `${shortUsd(low)}–${shortUsd(high).slice(1)}`;
}

/** `2026-10-14` -> `10-14` within the year shown, else `25-10-14`. */
export function shortDay(iso: string | null, today = new Date()): string {
  if (!iso) return "—";
  return iso.slice(0, 4) === String(today.getFullYear()) ? iso.slice(5) : iso.slice(2);
}

/** `Sale (Partial)` -> `Sale (part)`; the rest as filed. */
export function typeLabel(t: CongressTrade): string {
  if (t.side === "buy") return "Buy";
  const m = /^sale\s*\((partial|full)\)/i.exec(t.type);
  if (m) return m[1]!.toLowerCase() === "partial" ? "Sale (part)" : "Sale (full)";
  return t.type || "—";
}

export function memberName(t: CongressTrade): string {
  return `${t.firstName} ${t.lastName}`.trim();
}

/** `CA12` -> `CA-12`; a senator's empty district -> their state is not filed, so nothing. */
export function districtLabel(d: string | null): string {
  if (!d) return "";
  const m = /^([A-Z]{2})(\d+)$/.exec(d);
  return m ? `${m[1]}-${m[2]}` : d;
}

export type CongressLoad =
  | { state: "loading" }
  | { state: "none" }
  | { state: "ready"; data: CongressSymbol };

export function useCongress(symbol: string, days: number): CongressLoad {
  const [load, setLoad] = useState<CongressLoad>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setLoad({ state: "loading" });
    void loadCongress(symbol, days).then((r) => {
      if (live) setLoad(r ? { state: "ready", data: r } : { state: "none" });
    });
    return () => {
      live = false;
    };
  }, [symbol, days]);
  return load;
}
